/**
 * Dikey video grid'i icin tembel yukleyici.
 *
 * Videolar sayfa acilisinda indirilmez. Kaynak, video goruntu alanina
 * yaklastiginda atanir; oynatma yalnizca video gercekten ekranda gorunurken
 * surer. Boylece ana sayfa acilisinda onlarca MB'lik video trafigi olusmaz.
 */
class HomeVideoGrid extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.items = Array.from(this.querySelectorAll('[data-video-grid-item]'));
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.items.forEach((item) => this.setupItem(item));

    // Kaynak yalnizca video gercekten ekranda gorundugunde atanir. Onceden
    // yukleme yapmiyoruz: ziyaretcinin hic bakmadigi bir videoyu indirmek,
    // dosyalar buyuk oldugunda mobil veride ciddi maliyet olusturuyor.
    this.playObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const item = entry.target;
          if (entry.isIntersecting) {
            this.play(item);
          } else {
            this.pause(item, { auto: true });
          }
        });
      },
      { threshold: 0.35 }
    );

    this.items.forEach((item) => this.playObserver.observe(item));

    // Sekme arka plana alindiginda bosa video oynatmayalim.
    this.onVisibilityChange = () => {
      if (document.hidden) {
        this.items.forEach((item) => this.pause(item, { auto: true }));
      }
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  disconnectedCallback() {
    this.playObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  setupItem(item) {
    const video = item.querySelector('video');
    if (!video) return;

    // Kodek desteklenmiyorsa (ornegin HEVC/H.265 Chrome'da) veya ag hatasi olursa
    // video gizlenir ve poster gorseli kalici olarak gosterilir.
    const markUnavailable = () => {
      item.classList.add('is-unavailable');
      item.classList.remove('is-playing');
    };
    video.addEventListener('error', markUnavailable);
    video.querySelectorAll('source').forEach((source) => {
      source.addEventListener('error', () => {
        if (!video.currentSrc) markUnavailable();
      });
    });

    const toggle = item.querySelector('[data-video-grid-toggle]');
    if (toggle) {
      toggle.addEventListener('click', () => {
        if (video.paused) {
          // Kullanici acikca oynat dedi: kaynak henuz atanmadiysa simdi ata.
          this.loadVideo(item);
          this.userPaused = false;
          item.dataset.userPaused = 'false';
          this.play(item, { force: true });
        } else {
          item.dataset.userPaused = 'true';
          this.pause(item);
        }
      });
    }
  }

  loadVideo(item) {
    const video = item.querySelector('video');
    if (!video || video.dataset.loaded === 'true') return;

    const src = video.dataset.src;
    if (src) {
      video.src = src;
    } else {
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
    }

    // Burada load() cagirmiyoruz; indirmeyi play() baslatir, boylece
    // preload="none" davranisi korunur.
    video.dataset.loaded = 'true';
  }

  play(item, { force = false } = {}) {
    const video = item.querySelector('video');
    if (!video || item.classList.contains('is-unavailable')) return;

    // Hareketi azalt tercihi acikken kendiliginden oynatma yapmayiz;
    // kullanici dugmeye basarsa oynatiriz.
    if (!force && (this.reduceMotion || item.dataset.userPaused === 'true')) return;
    if (!force && item.dataset.autoplay !== 'true') return;

    this.loadVideo(item);

    const played = video.play();
    if (played?.catch) {
      played
        .then(() => item.classList.add('is-playing'))
        .catch(() => {
          // Tarayici oynatmayi engelledi; poster gorunur kalir.
          item.classList.remove('is-playing');
        });
    } else {
      item.classList.add('is-playing');
    }
  }

  pause(item, { auto = false } = {}) {
    const video = item.querySelector('video');
    if (!video) return;
    video.pause();
    item.classList.remove('is-playing');
    if (!auto) return;
    // Otomatik duraklatmada kullanici tercihini bozmayalim.
  }
}

if (!customElements.get('home-video-grid')) {
  customElements.define('home-video-grid', HomeVideoGrid);
}
